// src/layouts/MainLayout.js
import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { ACCOUNT_TYPE } from "../utils/constants";
import { logout } from "../services/operations/authAPI"
import {
    Menu,
    X,
    LogOut,
    Bell,
    CheckSquare,
    Building,
    ListChecks,
    BarChart,
    Home,
    User,
    Users,
    Settings,
    AreaChart,
    Verified, // Used for Closure
    ClipboardCheckIcon, // Used for Audits
} from 'lucide-react';

// Import the necessary API functions
import {
    getAssignedObservations, // Used for Closure notifications (Assigned/Rejected - for the person doing the closure)
    getUserAssignedAudits, // Used for Task Assigned notifications (Employee) & Submitted Observation notifications (for the auditor/verifier)
} from '../services/auditAPI';


export const Dashboard = () => {
    const { user } = useSelector((state) => state.profile);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const dispatch = useDispatch()
    const navigate = useNavigate();
    const location = useLocation();

    // State for notification counts for specific areas (optional, but good for detailed views)
    const [closureTaskAssignedCount, setClosureTaskAssignedCount] = useState(0); // Count for tasks assigned for closure
    const [assignedAuditsCount, setAssignedAuditsCount] = useState(0); // Count for audits assigned to the user (Employee)
    const [submittedForVerificationCount, setSubmittedForVerificationCount] = useState(0); // New state for submitted observations
    const [loadingNotifications, setLoadingNotifications] = useState(true);

    // State for the total notification count displayed on the bell
    const [totalNotificationCount, setTotalNotificationCount] = useState(0);

    // Define the polling interval (e.g., every 30 seconds)
    const POLLING_INTERVAL = 30000; // 30 seconds

    // Fetch notifications based on user role and relevant APIs
    const fetchNotifications = async (userId, accountType) => {
        if (!userId || !accountType) {
            console.warn("User ID or Account Type not available for fetching notifications.");
            setLoadingNotifications(false);
            return;
        }
        setLoadingNotifications(true);
        let closureAssigned = 0;
        let assignedAudits = 0;
        let submittedForVerification = 0; // Initialize the new counter

        try {
            // Fetch assigned observations for Closure notifications (relevant for the person assigned the closure task)
            // This API should return observations where the current user is the responsible person for closure.
            const assignedObservationsData = await getAssignedObservations(userId);
            // console.log('Assigned Observations Data (from getAssignedObservations) for counts:', assignedObservationsData);

            // Count observations that are 'Assigned' for closure verification (for the person doing the closure)
            assignedObservationsData.forEach(audit => {
                if (audit.assignedObservations && Array.isArray(audit.assignedObservations)) {
                    audit.assignedObservations.forEach(assignedObs => {
                        if (assignedObs.closureData?.status === 'Assigned') {
                            closureAssigned++;
                        }
                    });
                }
            });
            setClosureTaskAssignedCount(closureAssigned);


            // Fetch assigned audits (relevant for the auditor/manager who needs to see audit progress and submissions)
            // This API should return audits where the current user is part of the audit team.
            const userAssignedAudits = await getUserAssignedAudits(userId);
            // console.log('User Assigned Audits Data (from getUserAssignedAudits) for counts:', userAssignedAudits);

            // Count assigned audits (for Employee's 'Task Assigned' link)
            if (accountType === 'Employee') {
                 assignedAudits = userAssignedAudits.filter(audit =>
                    audit.status !== 'Completed' // Check if audit status is not Completed
                 ).length;
                 setAssignedAuditsCount(assignedAudits);
            }

            // Count Observation Submitted for Verification notifications (for the auditor/verifier)
            // Iterate through the report of the assigned audits
            userAssignedAudits.forEach(assignedAudit => {
                 if (assignedAudit.report && Array.isArray(assignedAudit.report)) {
                    assignedAudit.report.forEach(observationInReport => {
                        // Check if the observation has closure data and the status is 'Submitted'
                        if (observationInReport.closureData?.status === 'Submitted') {
                            submittedForVerification++; // Increment the new counter
                        }
                    });
                 }
            });
            setSubmittedForVerificationCount(submittedForVerification); // Update the new state


        } catch (error) {
            console.error("Failed to fetch notifications:", error);
            // Optionally show a message to the user
            setClosureTaskAssignedCount(0);
            setAssignedAuditsCount(0);
            setSubmittedForVerificationCount(0); // Reset the new state on error
        } finally {
            setLoadingNotifications(false);
            // Update the total count after fetching all relevant types
            setTotalNotificationCount(closureAssigned + assignedAudits + submittedForVerification);
        }
    };

    // Effect for initial fetch and setting up polling
    useEffect(() => {
        // Initial fetch
        fetchNotifications(user?._id, user?.accountType);

        // Set up polling if user is available
        let pollingIntervalId;
        if (user?._id && user?.accountType) {
            pollingIntervalId = setInterval(() => {
                fetchNotifications(user?._id, user?.accountType);
            }, POLLING_INTERVAL);
        }


        // Cleanup function to clear the interval when the component unmounts
        // or when user changes (which triggers a re-run of this effect)
        return () => {
            if (pollingIntervalId) {
                clearInterval(pollingIntervalId);
            }
        };
    }, [user?._id, user?.accountType]); // Dependencies: user ID and accountType

    // Note: The previous effect that refetched on route change is removed
    // because the polling effect now handles periodic updates regardless of route.
    // If specific pages need immediate updates *after* an action (like completing a task),
    // those pages should trigger a refetch of notifications directly or via a shared state mechanism.


    const handleLogout = async () => {
        try {
            dispatch(logout(navigate))
        } catch (error) {
            console.error("Logout failed:", error);
            // Optionally display an error message to the user
        }
    };

    const isActive = (path) => {
        return location.pathname === path || location.pathname.startsWith(`${path}/`);
    };

    const commonNavItems = [

     { path: '/audit/verify', icon: <Verified size={20} />, label: 'Closure' },
     { path: '/dashboard/my-profile', icon: <User size={20} />, label: 'Profile' },
     { path: '/dashboard/Settings', icon: <Settings size={20} />, label: 'Setting' },
   ];

   const superAdminNavItems = [
     { path: '/superadmin-dashboard', icon: <Home size={20} />, label: 'Dashboard' },
     { path: '/plants', icon: <Building size={20} />, label: 'Plant' },
     ...commonNavItems
   ];

   const adminNavItems = [
     { path: '/admin-dashboard', icon: <Home size={20} />, label: 'Dashboard' },
     // { path: '/admin/templates', icon: <ListChecks size={20} />, label: 'Templates' },
     { path: '/admin/checklist', icon: <CheckSquare size={20} />, label: 'Checklist' },
     { path: '/admin/audits', icon: <ClipboardCheckIcon size={20}/>, label: 'Audits' },
     { path: '/admin/area', icon: <AreaChart size={20} />, label: 'Area' },
     { path: '/admin/add-users', icon: <Users size={20} />, label: 'Users' },
     // { path: '/admin/safety-audit-report', icon: <BarChart size={20} />, label: 'Reports' },
     ...commonNavItems,
   ];

   const employeeNavItems = [
     { path: '/user-dashboard', icon: <Home size={20} />, label: 'Dashboard' },
     { path: '/user/audit-assigned', icon: <CheckSquare size={20} />, label: 'Task Assigned' },

     ...commonNavItems,
   ];

    // Use 'Employee' for the accountType check as per your API logic
    const navItems = user?.accountType === ACCOUNT_TYPE.SUPER_ADMIN ? superAdminNavItems : user?.accountType === ACCOUNT_TYPE.ADMIN ? adminNavItems : employeeNavItems;

    // Combine notification counts for sidebar items that might show multiple types
    const combinedNavItems = navItems.map(item => {
        let notificationCount = 0;
        if (item.path === '/audit/verify') {
            // Combine closure tasks assigned to the user and observations submitted for verification within their audits
            notificationCount = closureTaskAssignedCount 
        } else if (item.path === '/user/audit-assigned') {
            notificationCount = assignedAuditsCount;
        }
        // Add other specific counts for other paths if needed
        return { ...item, notificationCount };
    });


    if (!user) {
        // You might want a more sophisticated loading state or redirect here
        return <div>Loading or Not Authenticated...</div>;
    }

    return (
        <div className="flex bg-gray-50"style={{ height: 'calc(100vh - 65px)' }}>
            {/* Mobile sidebar backdrop */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 z-20 bg-black bg-opacity-50 transition-opacity lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <div
                className={`fixed inset-y-0 left-0 z-30 w-64 transform bg-white shadow-lg transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${
                    sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                }`}
            >
                <div className="flex h-16 items-center justify-between px-4 border-b border-gray-200">
                    <a href='/' className="flex items-center space-x-2">
                        <div className="bg-blue-100 p-1.5 rounded">
                            <CheckSquare className="h-5 w-5 text-blue-600" />
                        </div>
                        <span className="text-lg font-semibold text-gray-900">SafetyAudit</span>
                    </a>
                    <button
                        className="lg:hidden text-gray-500 hover:text-gray-700 focus:outline-none"
                        onClick={() => setSidebarOpen(false)}
                    >
                        <X size={24} />
                    </button>
                </div>

                <nav className="mt-5 px-2 space-y-1">
                    {combinedNavItems.map((item) => ( 
                        <Link
                            key={item.path}
                            to={item.path}
                            className={`flex items-center px-4 py-2.5 text-sm font-medium rounded-md transition-colors relative ${ // Added relative for absolute positioning of dot
                                isActive(item.path)
                                    ? 'bg-blue-50 text-blue-700'
                                    : 'text-gray-700 hover:bg-gray-100'
                            }`}
                            onClick={() => setSidebarOpen(false)}
                        >
                            <span className="mr-3">{item.icon}</span>
                            {item.label}
                            {/* Conditional Notification Dot for sidebar items */}
                             {item.notificationCount > 0 && (
                                 <span className="ml-1 h-2 w-2 rounded-full bg-red-600"></span> // Changed to a simple red dot
                             )}
                        </Link>
                    ))}
                </nav>

                <div className="absolute bottom-1 w-full p-4 border-gray-200">
                    <div className="flex items-center mb-4">
                        {/* User profile info could go here */}
                    </div>
                    <button
                        onClick={handleLogout}
                        className="w-full py-2.5 text-sm font-medium rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none flex items-center justify-center space-x-2 border border-gray-300"
                    >
                        <LogOut size={16} />
                        <span>Sign Out</span>
                    </button>
                </div>
            </div>

            {/* Main content */}
            <div className="flex flex-col flex-1 overflow-hidden">
                {/* Top header */}
                <header className="bg-white shadow-sm border-b border-gray-200">
                    <div className="flex h-16 items-center justify-between px-4">
                        {/* Mobile menu button (only visible on small screens) */}
                        <button
                            className="text-gray-500 focus:outline-none lg:hidden"
                            onClick={() => setSidebarOpen(true)}
                        >
                            <Menu size={24} />
                        </button>

                        {/* Placeholder for potential center content on larger screens */}
                        <div className="flex-1 hidden lg:block ">
                            {/* Add logo or title for larger screens if needed */}
                        </div>

                        {/* Notification icon (visible on all screen sizes) */}
                         <div className="flex items-center space-x-4 ">
                             <div className="relative p-1 rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none">
                             {/* Link to a notifications page if you have one, otherwise just the icon */}
                             <Link to='/audit/notification'>
                                 <Bell size={20} />
                                 {/* Conditional Notification Dot with total count */}
                                 {totalNotificationCount > 0 && (
                                     <span className="absolute top-0 right-0 block h-4 w-4 rounded-full bg-red-500 text-xs text-white text-center flex items-center justify-center">
                                          {totalNotificationCount}
                                     </span>
                                 )}
                             </Link>
                             </div>
                         </div>
                    </div>
                </header>

                {/* Page content */}
                <main className="flex-1 overflow-auto p-4 bg-white ">
                    <Outlet />
                </main>
            </div>
        </div>
    );
};
